/* eslint-disable */
import React, { useEffect, useId, useRef } from "react";
import { Button, ConfigProvider } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import styled, { css, keyframes } from "styled-components";
import { Icon } from "../Icon/Icon";
import { createPortal } from "react-dom";
import { MOBILE_GUTTER, PHONE_MAX_WIDTH, SAFE_AREA, TAP_TARGET, mediaPhone } from "@/shared/theme/breakpoints";
import { PHONE_DOCK_HEIGHT, phoneFullscreenDialog, phoneSheetDialog } from "./phoneSheet";
import { pressedDarken } from "@/shared/theme/pressed";
import { KEYBOARD_OPEN_ATTRIBUTE, hideWhileTyping } from "@/shared/theme/keyboard";

/* ─── Variant themes ─── */

const VARIANT_THEMES = {
	default: {
		bg: "#f3f5ff",
		shadow: "0px 12px 24px 0px #1559a53d",
		primaryBg: "#3a0ca3",
		primaryHover: "#3a0ca3",
		outlineBorder: "#3a0ca3",
		outlineColor: "#3a0ca3",
	},
	danger: {
		bg: "#ffebef",
		shadow: "0px 12px 24px 0px #d800273d",
		primaryBg: "#D30024",
		primaryHover: "#D30024",
		outlineBorder: "#2e1a89",
		outlineColor: "#2e1a89",
	},
	/** The default neutral sheet with a red confirm, for a destructive step that isn't an alert. */
	destructive: {
		bg: "#f3f5ff",
		shadow: "0px 12px 24px 0px #1559a53d",
		primaryBg: "#d80027",
		primaryHover: "#c10022",
		outlineBorder: "#3a0ca3",
		outlineColor: "#3a0ca3",
	},
} as const;

type Variant = keyof typeof VARIANT_THEMES;

/**
 * Phone (≤768px) presentation. Above 768px every mode renders the same centred dialog.
 * "viewer" is a full-screen view of one thing (a document): a white app bar whose title brings its own Back
 * control instead of ✕, a body that fills the screen without scrolling (the content scrolls itself), no action
 * row, and an optional `dock` panel under it.
 */
export type DynamicModalMobileMode = "sheet" | "fullscreen" | "dialog" | "viewer";

const isFullScreen = (mode: DynamicModalMobileMode) => mode === "fullscreen" || mode === "viewer";

/** Applies `rules` on phones only when the modal uses a sheet or full-screen layout. */
const phoneLayout =
	(rules: ReturnType<typeof css>) =>
	({ $mobile }: { $mobile: DynamicModalMobileMode }) =>
		$mobile !== "dialog" && mediaPhone`${rules}`;

/* ─── Styled components ─── */

const overlayFade = keyframes`
	from { opacity: 0; }
	to { opacity: 1; }
`;

const dialogPop = keyframes`
	from { opacity: 0; transform: translateY(-8px) scale(0.98); }
	to { opacity: 1; transform: translateY(0) scale(1); }
`;

const DIALOG_Z_INDEX = 2201;

/**
 * antd popups (Select, DatePicker, Tooltip) inside the modal render into PopupLayer, above the
 * dialog and outside its scrolling body, so neither clips nor page-wide z-index rules (e.g.
 * ManageCampaign's phone popups at 2150) hide them. A popup that portals to document.body itself
 * via its own getPopupContainer still needs a base above the dialog.
 */
const POPUP_THEME = { token: { zIndexPopupBase: DIALOG_Z_INDEX + 10 } };

const Overlay = styled.div<{ $mobile: DynamicModalMobileMode }>`
	position: fixed;
	inset: 0;
	z-index: 2200;
	display: flex;
	align-items: center;
	justify-content: center;
	animation: ${overlayFade} 160ms ease forwards;

	${({ $mobile }) => $mobile === "sheet" && mediaPhone`align-items: flex-end;`}
	${({ $mobile }) => isFullScreen($mobile) && mediaPhone`align-items: stretch;`}
`;

const Mask = styled.div`
	position: fixed;
	inset: 0;
	background: rgba(0, 0, 0, 0.45);
	z-index: 2200;
`;

const PopupLayer = styled.div`
	position: absolute;
	inset: 0;
	z-index: ${DIALOG_Z_INDEX + 1};
	pointer-events: none;

	& > * {
		pointer-events: auto;
	}
`;

const Dialog = styled.div<{
	$width: number;
	$variant: Variant;
	$maxHeight?: string;
	$mobile: DynamicModalMobileMode;
}>`
	position: relative;
	z-index: ${DIALOG_Z_INDEX};
	width: ${({ $width }) => $width}px;
	max-width: calc(100% - 48px);
	max-height: ${({ $maxHeight }) => $maxHeight || "calc(100vh - 48px)"};
	display: flex;
	flex-direction: column;
	/* The body slot scrolls on short screens (see bodyScroll); this catches a modal without one.
	   The phone sheet and fullscreen layouts set overflow: hidden and scroll their content instead. */
	overflow-y: auto;
	/* Vertical only: overflow-y: auto would otherwise let a few stray pixels show a horizontal bar. */
	overflow-x: hidden;
	overscroll-behavior: contain;
	border-radius: 24px;
	background: ${({ $variant }) => VARIANT_THEMES[$variant].bg};
	box-shadow: ${({ $variant }) => VARIANT_THEMES[$variant].shadow};
	outline: none;
	transform-origin: center center;
	animation: ${dialogPop} 180ms cubic-bezier(0.2, 0, 0, 1) forwards;

	/* Remove default focus outline / ring for the dialog itself so
		 focusing the modal (for keyboard handling) doesn't add a visible border. */
	&:focus,
	&:focus-visible {
		outline: none;
		box-shadow: ${({ $variant }) => VARIANT_THEMES[$variant].shadow};
	}

	${({ $mobile }) => $mobile === "sheet" && mediaPhone`${phoneSheetDialog}`}
	${({ $mobile }) => isFullScreen($mobile) && mediaPhone`${phoneFullscreenDialog}`}
`;

const ModalContent = styled.div<{ $scrollable?: boolean; $maxHeight?: string; $mobile: DynamicModalMobileMode }>`
	position: relative;
	padding: 32px;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	width: 100%;
	flex: 1 1 auto;
	min-height: 0;
	max-height: ${({ $maxHeight }) => $maxHeight || "none"};
	overflow-y: ${({ $scrollable }) => ($scrollable ? "auto" : "visible")};
	${({ $scrollable }) => $scrollable && "overflow-x: hidden;"}

	/* The body scrolls between the sticky header and the pinned actions. */
	${phoneLayout(css`
		flex: 1 1 auto;
		min-height: 0;
		max-height: none;
		padding: 0 ${MOBILE_GUTTER}px;
		/* Only ever vertical: anything too wide is clipped rather than shifting the sticky header and actions. */
		overflow-x: hidden;
		overflow-y: auto;
		overscroll-behavior: contain;
	`)}

	/* Viewer: the content fills the space under the app bar and scrolls itself, so the body never grows or
	   scrolls (zooming a document must not bring up a page scrollbar). Without a dock, it stops above the
	   home indicator. */
	${({ $mobile }) =>
		$mobile === "viewer" &&
		mediaPhone`
			padding: 0 0 ${SAFE_AREA.bottom};
			overflow: hidden;
			align-items: stretch;

			&:has(+ [data-modal-dock]) {
				padding-bottom: 0;
			}
		`}
`;

const CloseBtn = styled.button<{ $phoneOnly: boolean }>`
	position: absolute;
	top: 16px;
	right: 16px;
	width: 32px;
	height: 32px;
	border: none;
	border-radius: 50%;
	background: transparent;
	color: #666;
	cursor: pointer;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	font-size: 18px;

	&:hover:not(:disabled) {
		background: rgba(0, 0, 0, 0.06);
	}

	&:disabled {
		cursor: not-allowed;
		opacity: 0.5;
	}

	${mediaPhone`
		width: ${TAP_TARGET}px;
		height: ${TAP_TARGET}px;
	`}

	/* showCloseButton={false} hides the control above the phone breakpoint only. */
	${({ $phoneOnly }) =>
		$phoneOnly &&
		css`
			@media (min-width: ${PHONE_MAX_WIDTH + 1}px) {
				display: none;
			}
		`}

	${pressedDarken}
`;

const HeaderContainer = styled.div<{
	$centered?: boolean;
	$variant: Variant;
	$mobile: DynamicModalMobileMode;
}>`
	display: flex;
	flex-direction: column;
	gap: 16px;
	margin-bottom: 24px;
	width: 100%;
	align-items: ${({ $centered }) => ($centered ? "center" : "flex-start")};

	/* Sticky header; the side padding keeps the title clear of the close control. */
	${({ $variant, $centered }) =>
		phoneLayout(css`
			position: sticky;
			top: 0;
			z-index: 2;
			margin-bottom: 8px;
			padding: 32px ${TAP_TARGET}px 16px ${$centered ? TAP_TARGET : 0}px;
			background: ${VARIANT_THEMES[$variant].bg};
		`)}

	${CloseBtn} {
		${phoneLayout(css`
			top: 22px;
			right: -12px;
		`)}
	}

	/* Viewer app bar: a white strip across the full width; the title brings its own Back control. */
	${({ $mobile }) =>
		$mobile === "viewer" &&
		mediaPhone`
			flex: 0 0 auto;
			margin: 0;
			padding: 4px 8px;
			border-bottom: 1px solid #e6e3f2;
			background: #fff;

			${CloseBtn} {
				display: none;
			}
		`}
`;

const Title = styled.div<{ $centered?: boolean }>`
	font-size: 24px;
	font-weight: 600;
	color: #000;
	line-height: 1.2;
	text-align: ${({ $centered }) => ($centered ? "center" : "left")};

	/* The header sizes the title to its content; uncapped, a long name (or one cut with an ellipsis) sizes to
	   its full text and widens the sheet. */
	${mediaPhone`
		max-width: 100%;
		font-size: 18px;
		overflow-wrap: anywhere;
	`}
`;

const Description = styled.div<{ $centered?: boolean }>`
	font-size: 15px;
	color: #555;
	line-height: 1.55;
	margin-top: -16px;
	margin-bottom: 16px;
	width: 100%;
	text-align: ${({ $centered }) => ($centered ? "center" : "left")};

	/* The phone sheet's header is sticky and opaque, so the desktop pull-up would hide the first line. */
	${mediaPhone`margin-top: 0;`}
`;

/**
 * For a list with its own scroll inside the modal body: put modalShrink on every wrapper between
 * the body and the list, and modalScrollList on the list. On short screens the list then shrinks
 * to fit instead of the body scrolling around it. Needs bodyScroll to stay a flex column.
 */
export const modalShrink = css`
	display: flex;
	flex-direction: column;
	min-height: 0;
`;

export const modalScrollList = (maxHeight: number, minHeight = 120) => css`
	max-height: ${maxHeight}px;
	min-height: ${minHeight}px;
	overflow-y: auto;
`;

/** Short screens: only the body scrolls, between the title and the pinned actions. */
const bodyScroll = css`
	flex: 1 1 auto;
	min-height: 0;
	overflow-y: auto;
	overflow-x: hidden;
	/* A column so content with its own scroll area can shrink into the space (modalShrink,
	   modalScrollList) instead of scrolling inside this one; everything else keeps its height. */
	display: flex;
	flex-direction: column;
	overscroll-behavior: contain;
	/* Room for the inputs' 2px focus ring, which the scroll box would otherwise clip. */
	width: calc(100% + 8px);
	margin: -4px -4px 20px;
	padding: 4px;
`;

const ChildrenWrapper = styled.div<{ $mobile: DynamicModalMobileMode }>`
	width: 100%;
	margin-bottom: 24px;

	${({ $mobile }) =>
		$mobile === "dialog"
			? bodyScroll
			: css`
					@media (min-width: ${PHONE_MAX_WIDTH + 1}px) {
						${bodyScroll}
					}
				`}

	/* Viewer: the content gets all the room between the app bar and the dock, at a fixed size. */
	${({ $mobile }) =>
		$mobile === "viewer" &&
		mediaPhone`
			position: relative;
			display: flex;
			flex: 1 1 auto;
			flex-direction: column;
			min-height: 0;
			margin-bottom: 0;
		`}

	/* ─── Default input styles for Ant Design form controls ─── */

	.ant-form-item-label > label {
		color: #707070 !important;
		font-weight: 400 !important;
		height: auto !important;
		line-height: 1.2 !important;
		padding-bottom: 4px !important;
		font-size: 14px !important;
	}

	.ant-form-item {
		margin-bottom: 16px !important;
	}

	.ant-form-item-label {
		padding: 0 !important;
	}

	/* Shared base for all input-like controls */
	.ant-input,
	.ant-input-number,
	.ant-input-affix-wrapper,
	.ant-picker,
	.ant-select-selector {
		border: 1px solid #cdd4f6 !important;
		border-radius: 8px !important;
		padding: 0 12px !important;
		height: 44px !important;
		display: flex !important;
		align-items: center !important;
		font-size: 15px !important;
		background: #fff !important;
		transition:
			border-color 0.2s,
			box-shadow 0.2s;
		width: 100% !important;
	}

	.ant-select {
		width: 100% !important;
		margin-bottom: 12px !important;
	}

	/* InputNumber inner input alignment */
	.ant-input-number-input {
		height: 42px !important;
		font-size: 15px !important;
		padding: 0 !important;
	}

	/* Select specific fixes */
	.ant-select .ant-select-selector {
		padding-right: 40px !important;
	}

	.ant-select-selection-item,
	.ant-select-selection-placeholder {
		padding: 0 !important;
		line-height: 44px !important;
		display: flex !important;
		align-items: center !important;
	}

	.ant-select-single .ant-select-arrow {
		inset-inline-end: 12px !important;
		top: 0 !important;
		transform: none !important;
		height: 44px !important;
		width: 16px !important;
		margin: 0 !important;
		display: flex !important;
		align-items: center !important;
		justify-content: center !important;
		line-height: 1 !important;
	}

	.ant-select-selection-search {
		display: flex !important;
		align-items: center !important;
	}

	/* TextArea — auto height, keep border-radius */
	.ant-input[type="textarea"],
	textarea.ant-input {
		height: auto !important;
		min-height: 80px;
		padding: 12px !important;
	}

	/* Focus / hover ring */
	.ant-input:focus,
	.ant-input-focused,
	.ant-input:hover,
	.ant-input-number:hover,
	.ant-input-number-focused,
	.ant-input-affix-wrapper:hover,
	.ant-input-affix-wrapper-focused,
	.ant-picker:hover,
	.ant-picker-focused,
	.ant-select:not(.ant-select-disabled):hover .ant-select-selector,
	.ant-select-focused .ant-select-selector {
		border-color: #3a0ca3 !important;
		box-shadow: 0 0 0 2px rgba(58, 12, 163, 0.1) !important;
	}

	/* Placeholder color */
	.ant-input::placeholder,
	.ant-input-number-input::placeholder,
	.ant-select-selection-placeholder {
		color: #aaa !important;
	}

	/* Remove bottom margin from last form item to avoid double spacing before buttons */
	.ant-form-item:last-child {
		margin-bottom: 0 !important;
	}

	/* iOS Safari zooms into inputs below 16px on focus. */
	${mediaPhone`
		.ant-input,
		.ant-input-number,
		.ant-input-number-input,
		.ant-input-affix-wrapper,
		.ant-picker,
		.ant-picker input,
		.ant-select-selector,
		.ant-select-selection-search-input {
			font-size: 16px !important;
		}
	`}
`;

const ButtonGroup = styled.div<{
	$single?: boolean;
	$fullWidth?: boolean;
	$compact?: boolean;
	$variant: Variant;
	$mobile: DynamicModalMobileMode;
}>`
	display: flex;
	justify-content: ${({ $compact }) => ($compact ? "center" : "space-between")};
	gap: 16px;
	margin-left: ${({ $compact }) => ($compact ? "0" : "auto")};
	align-items: center;
	width: 100%;
	max-width: ${({ $single, $fullWidth, $compact }) =>
		$compact ? "364px" : $single || $fullWidth ? "none" : "520px"};
	font-size: 16px;
	align-self: ${({ $compact }) => ($compact ? "center" : "stretch")};

	/* Actions stay pinned to the bottom of the sheet; primary wraps above secondary. */
	${({ $variant }) =>
		phoneLayout(css`
			position: sticky;
			bottom: 0;
			z-index: 1;
			flex-wrap: wrap-reverse;
			gap: 12px;
			max-width: none;
			margin-top: auto;
			padding: 16px 0 calc(24px + ${SAFE_AREA.bottom});
			background: ${VARIANT_THEMES[$variant].bg};
		`)}

	/* The keyboard would lift the pinned actions over the field being typed in. */
	${({ $mobile }) => $mobile !== "dialog" && hideWhileTyping()}
`;

/** Viewer: the panel docked under the content (comments), about half the screen. */
const Dock = styled.section`
	position: relative;
	display: flex;
	flex: 0 0 auto;
	flex-direction: column;
	height: ${PHONE_DOCK_HEIGHT};
	padding-bottom: ${SAFE_AREA.bottom};
	border-top: 1px solid #dde2fa;
	border-radius: 20px 20px 0 0;
	background: #fff;
	box-shadow: 0 -8px 24px rgba(21, 89, 165, 0.12);

	/* While typing, the dock takes the whole screen under the app bar rather than half of what the keyboard
	   leaves; the content steps aside. */
	html[${KEYBOARD_OPEN_ATTRIBUTE}] & {
		flex: 1 1 auto;
		height: auto;
		border-radius: 0;
	}

	html[${KEYBOARD_OPEN_ATTRIBUTE}] [role="dialog"]:has(&) [data-modal-body] > :not(:first-child) {
		display: none;
	}
`;

const OutlineButton = styled(Button)<{ $variant: Variant; $actionHeight: number }>`
	flex: 1;
	height: ${({ $actionHeight }) => $actionHeight}px;
	border-radius: 24px;
	font-size: 16px;
	font-weight: 600;
	border: 2px solid ${({ $variant }) => VARIANT_THEMES[$variant].outlineBorder} !important;
	color: ${({ $variant }) => VARIANT_THEMES[$variant].outlineColor} !important;
	background: #fff;
	transition: all 0.2s !important;

	&:hover,
	&:focus-visible {
		box-shadow: inset 0 0 0 2px ${({ $variant }) => VARIANT_THEMES[$variant].outlineBorder} !important;
		border-color: ${({ $variant }) => VARIANT_THEMES[$variant].outlineBorder} !important;
		color: ${({ $variant }) => VARIANT_THEMES[$variant].outlineColor} !important;
	}

	&:disabled,
	&.ant-btn-disabled {
		border-color: #d9d9d9 !important;
		color: #8c8c8c !important;
		background: #f5f5f5 !important;
		cursor: not-allowed;
		opacity: 1 !important;
		box-shadow: none !important;
	}

	${pressedDarken}
`;

const PrimaryButton = styled(Button)<{ $variant: Variant; $actionHeight: number; $pill?: boolean }>`
	flex: 1;
	height: ${({ $actionHeight }) => $actionHeight}px;
	border-radius: ${({ $pill }) => ($pill ? "64px" : "24px")};
	font-weight: 600;
	font-size: 16px;
	background: ${({ $variant }) => VARIANT_THEMES[$variant].primaryBg} !important;
	border: none;
	color: #fff !important;
	text-decoration: none !important;

	&:hover {
		background: ${({ $variant }) => VARIANT_THEMES[$variant].primaryHover} !important;
		opacity: 0.9;
	}

	&:disabled,
	&.ant-btn-disabled {
		background: #d9d9d9 !important;
		border-color: #d9d9d9 !important;
		color: #8c8c8c !important;
		cursor: not-allowed;
		opacity: 1 !important;
	}

	${pressedDarken}
`;

/* ─── Public interface ─── */

export interface DynamicModalProps {
	open: boolean;
	onCancel: () => void;
	title: React.ReactNode;
	description?: React.ReactNode;
	icon?: { src: string; color?: string; size?: number } | React.ReactNode;
	/** Body slot — renders between description & buttons. Use for forms, inputs, etc. */
	children?: React.ReactNode;
	variant?: Variant;
	/** Primary action button label (default: "Confirm") */
	confirmText?: string;
	onConfirm: () => void;
	confirmDisabled?: boolean;
	loading?: boolean;
	/** Secondary action button label (default: "Cancel") */
	cancelText?: string;
	/**
	 * Secondary button handler when it does something other than dismiss (e.g. "Discard and
	 * Close"). Escape, the mask and the close button still call `onCancel`.
	 */
	onSecondary?: () => void;
	width?: number;
	/** Center heading, description and icon; used by the creator welcome experience. */
	centered?: boolean;
	/** Use a single outlined confirmation control instead of the standard button pair. */
	singleOutlineAction?: boolean;
	/** Use a single filled confirmation control instead of the standard button pair. */
	singlePrimaryAction?: boolean;
	/** Render the primary control as a native link, e.g. mailto:. `onConfirm` still runs on click. */
	confirmHref?: string;
	/** Prevent dismissal until the required action completes. */
	closable?: boolean;
	/**
	 * Render the top-right close control independently of Escape/mask dismissal.
	 * Phones always show it for dismissible modals; `false` only hides it above 768px.
	 */
	showCloseButton?: boolean;
	/** Override the shared 48px action height for a specific design contract. */
	actionHeight?: number;
	/** Let paired action buttons span the full modal content width. */
	fullWidthActions?: boolean;
	/** Allow tall modal content to scroll within the viewport. */
	scrollable?: boolean;
	maxHeight?: string;
	/**
	 * Phone (≤768px) layout: a bottom sheet sized to its content (default), a full-screen
	 * view for content that needs the whole screen, or the unchanged centred dialog.
	 */
	mobile?: DynamicModalMobileMode;
	/**
	 * `mobile="viewer"` only: a panel docked under the content (e.g. comments), about half the screen and the
	 * whole screen while typing. The content shrinks to make room. Leave it out to hide the panel.
	 */
	dock?: React.ReactNode;
}

export const DynamicModal: React.FC<DynamicModalProps> = ({
	open,
	onCancel,
	onSecondary,
	title,
	description,
	icon,
	children,
	variant = "default",
	confirmText = "Confirm",
	onConfirm,
	confirmDisabled = false,
	loading = false,
	cancelText = "Cancel",
	width = 432,
	centered = false,
	singleOutlineAction = false,
	singlePrimaryAction = false,
	confirmHref,
	closable = true,
	showCloseButton = true,
	actionHeight = 48,
	fullWidthActions = false,
	scrollable = false,
	maxHeight,
	mobile = "sheet",
	dock,
}) => {
	const isViewer = mobile === "viewer";
	const keyboard = !loading && closable;
	const maskClosable = !loading && closable;
	const dialogRef = useRef<HTMLDivElement | null>(null);
	const popupLayerRef = useRef<HTMLDivElement | null>(null);
	const prevActiveRef = useRef<HTMLElement | null>(null);
	const titleId = useId();
	const descriptionId = useId();

	// Stable ref for onCancel to avoid re-running the effect when the callback identity changes.
	// This prevents focus-stealing when a parent re-renders (e.g. during typing in child inputs).
	const onCancelRef = useRef(onCancel);
	onCancelRef.current = onCancel;

	useEffect(() => {
		if (!open) return;
		prevActiveRef.current = document.activeElement as HTMLElement | null;
		// lock body scroll
		const prevOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";

		// focus the dialog for keyboard handling
		setTimeout(() => dialogRef.current?.focus(), 0);

		const focusableSelector =
			'a[href], area[href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex]:not([tabindex="-1"]), [contenteditable=true]';

		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape" && keyboard) {
				e.stopPropagation();
				onCancelRef.current();
				return;
			}
			if (e.key === "Tab") {
				const root = dialogRef.current;
				if (!root) return;
				const nodes = Array.from(root.querySelectorAll<HTMLElement>(focusableSelector)).filter(
					(el) =>
						(el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0) &&
						!el.hasAttribute("disabled"),
				);
				if (nodes.length === 0) {
					e.preventDefault();
					return;
				}
				const first = nodes[0];
				const last = nodes[nodes.length - 1];
				const active = document.activeElement as HTMLElement | null;
				if (e.shiftKey) {
					if (active === first || active === root) {
						e.preventDefault();
						last.focus();
					}
				} else {
					if (active === last) {
						e.preventDefault();
						first.focus();
					}
				}
			}
		};

		document.addEventListener("keydown", onKeyDown, true);
		return () => {
			document.removeEventListener("keydown", onKeyDown, true);
			// restore scroll
			document.body.style.overflow = prevOverflow;
			// restore focus
			try {
				prevActiveRef.current?.focus();
			} catch (e) {
				/* ignore */
			}
		};
	}, [open, keyboard]);

	if (!open) return null;

	return createPortal(
		<Overlay $mobile={mobile}>
			<Mask onClick={() => maskClosable && !loading && onCancel()} />
			<Dialog
				ref={dialogRef}
				role="dialog"
				aria-modal
				aria-labelledby={titleId}
				aria-describedby={description ? descriptionId : undefined}
				tabIndex={-1}
				$width={width}
				$variant={variant}
				$maxHeight={maxHeight}
				$mobile={mobile}
				onClick={(e) => e.stopPropagation()}
			>
				<ConfigProvider theme={POPUP_THEME} getPopupContainer={() => popupLayerRef.current ?? document.body}>
					<ModalContent data-modal-body $scrollable={scrollable} $maxHeight={maxHeight} $mobile={mobile}>
						<HeaderContainer $centered={centered} $variant={variant} $mobile={mobile}>
							{/* Inside the header so it stays put while a phone sheet scrolls; on desktop
						    it is still positioned against ModalContent. */}
							{closable && (
								<CloseBtn
									aria-label="Close"
									onClick={onCancel}
									disabled={loading}
									$phoneOnly={!showCloseButton}
								>
									<CloseOutlined />
								</CloseBtn>
							)}
							{icon && (
								<div
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: centered ? "center" : undefined,
									}}
								>
									{typeof icon === "object" && icon !== null && "src" in icon ? (
										<Icon src={icon.src} size={icon.size ?? 32} color={icon.color} />
									) : (
										icon
									)}
								</div>
							)}
							<Title id={titleId} $centered={centered}>
								{title}
							</Title>
						</HeaderContainer>

						{description && (
							<Description id={descriptionId} $centered={centered}>
								{description}
							</Description>
						)}

						{children && <ChildrenWrapper $mobile={mobile}>{children}</ChildrenWrapper>}

						{!isViewer && (
							<ButtonGroup
								$single={singleOutlineAction || singlePrimaryAction}
								$fullWidth={fullWidthActions}
								$compact={singlePrimaryAction}
								$variant={variant}
								$mobile={mobile}
							>
								{singleOutlineAction ? (
									<OutlineButton
										$variant={variant}
										$actionHeight={actionHeight}
										onClick={onConfirm}
										disabled={confirmDisabled || loading}
										loading={loading}
									>
										{confirmText}
									</OutlineButton>
								) : singlePrimaryAction ? (
									<PrimaryButton
										$variant={variant}
										$actionHeight={actionHeight}
										$pill
										type="primary"
										href={confirmHref}
										target={confirmHref ? "_blank" : undefined}
										rel={confirmHref ? "noopener noreferrer" : undefined}
										onClick={onConfirm}
										disabled={confirmDisabled || loading}
										loading={loading}
									>
										{confirmText}
									</PrimaryButton>
								) : (
									<>
										<OutlineButton
											$variant={variant}
											$actionHeight={actionHeight}
											onClick={onSecondary ?? onCancel}
											disabled={loading}
										>
											{cancelText}
										</OutlineButton>
										<PrimaryButton
											$variant={variant}
											$actionHeight={actionHeight}
											type="primary"
											href={confirmHref}
											target={confirmHref ? "_blank" : undefined}
											rel={confirmHref ? "noopener noreferrer" : undefined}
											onClick={onConfirm}
											disabled={confirmDisabled || loading}
											loading={loading}
										>
											{confirmText}
										</PrimaryButton>
									</>
								)}
							</ButtonGroup>
						)}
					</ModalContent>
					{isViewer && dock ? <Dock data-modal-dock>{dock}</Dock> : null}
				</ConfigProvider>
			</Dialog>
			<PopupLayer ref={popupLayerRef} />
		</Overlay>,
		document.body,
	);
};
